// Véhicules de collecte (régie et prestataires) et prestataires (fiche simple) ; création par le
// responsable technique (codes générés).
import { schemaCreationPrestataire, schemaCreationVehicule } from '@cc/contrats';
import { Bandeau, Bouton, Champ, Dialogue, EtatVide, LienBouton, PuceEtat, Selection, Tableau, type ColonneTableau } from '@cc/ui';
import { ArrowLeft, Plus, Truck } from 'lucide-react';
import { useState } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { creerPrestataire, creerVehicule, INVALIDER_PROPRETE, usePrestataires, useVehicules } from './api';
import type { Prestataire, Vehicule } from './types';
import styles from './proprete.module.css';
import commun from '../../commun/commun.module.css';

function FormulaireVehicule({ prestataires, onFermer }: { prestataires: Prestataire[]; onFermer: () => void }) {
  const [v, setV] = useState({ immatriculation: '', type: '', capacite: '', prestataire_id: '' });
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const action = useAction({
    executer: () => {
      const corps = schemaCreationVehicule.safeParse({ immatriculation: v.immatriculation, type: v.type, capacite_m3: v.capacite ? Number(v.capacite) : undefined, prestataire_id: v.prestataire_id || undefined });
      if (!corps.success) {
        setErreurs(Object.fromEntries(corps.error.issues.map((i) => [String(i.path[0]), i.message])));
        return Promise.reject(new Error('Formulaire incomplet'));
      }
      setErreurs({});
      return creerVehicule(corps.data);
    },
    succes: (x) => `Véhicule ${x.immatriculation} créé, code ${x.code}.`,
    invalider: INVALIDER_PROPRETE,
    apres: onFermer,
  });
  const champ = (nom: string) => action.erreursChamps.find((e) => e.champ === nom)?.message ?? erreurs[nom];
  return (
    <Dialogue ouvert titre="Nouveau véhicule" onFermer={onFermer} actions={<><Bouton variante="secondaire" onClick={onFermer}>Annuler</Bouton><Bouton variante="principal" chargement={action.enCours} onClick={() => action.lancer(undefined)}>Créer le véhicule</Bouton></>}>
      <div className={styles.grilleFormulaire}>
        <Champ libelle="Immatriculation" value={v.immatriculation} onChange={(e) => setV((x) => ({ ...x, immatriculation: e.target.value }))} erreur={champ('immatriculation')} required />
        <Champ libelle="Type" value={v.type} onChange={(e) => setV((x) => ({ ...x, type: e.target.value }))} erreur={champ('type')} required aide="Par exemple : benne à ordures ménagères" />
        <Champ libelle="Capacité (m³)" type="number" value={v.capacite} onChange={(e) => setV((x) => ({ ...x, capacite: e.target.value }))} erreur={champ('capacite_m3')} facultatif />
        <Selection libelle="Prestataire" options={prestataires.map((p) => ({ valeur: p.id, libelle: p.nom }))} optionVide="Régie communale" value={v.prestataire_id} onChange={(e) => setV((x) => ({ ...x, prestataire_id: e.target.value }))} facultatif />
      </div>
      {action.messageErreur && action.messageErreur !== 'Formulaire incomplet' ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

function FormulairePrestataire({ onFermer }: { onFermer: () => void }) {
  const [v, setV] = useState({ nom: '', contact: '', contrat: '' });
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const action = useAction({
    executer: () => {
      const corps = schemaCreationPrestataire.safeParse({ nom: v.nom, contact: v.contact || undefined, contrat_reference: v.contrat || undefined });
      if (!corps.success) {
        setErreurs(Object.fromEntries(corps.error.issues.map((i) => [String(i.path[0]), i.message])));
        return Promise.reject(new Error('Formulaire incomplet'));
      }
      setErreurs({});
      return creerPrestataire(corps.data);
    },
    succes: (p) => `Prestataire ${p.nom} créé, code ${p.code}.`,
    invalider: INVALIDER_PROPRETE,
    apres: onFermer,
  });
  const champ = (nom: string) => action.erreursChamps.find((e) => e.champ === nom)?.message ?? erreurs[nom];
  return (
    <Dialogue ouvert titre="Nouveau prestataire" description="Fiche simple : les marchés publics et contrats relèvent d'un lot ultérieur." onFermer={onFermer} actions={<><Bouton variante="secondaire" onClick={onFermer}>Annuler</Bouton><Bouton variante="principal" chargement={action.enCours} onClick={() => action.lancer(undefined)}>Créer le prestataire</Bouton></>}>
      <Champ libelle="Nom" value={v.nom} onChange={(e) => setV((x) => ({ ...x, nom: e.target.value }))} erreur={champ('nom')} required />
      <Champ libelle="Contact" value={v.contact} onChange={(e) => setV((x) => ({ ...x, contact: e.target.value }))} facultatif />
      <Champ libelle="Référence du contrat" value={v.contrat} onChange={(e) => setV((x) => ({ ...x, contrat: e.target.value }))} facultatif />
      {action.messageErreur && action.messageErreur !== 'Formulaire incomplet' ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

export function PageVehicules() {
  const { contexte } = useSession();
  const vehicules = useVehicules();
  const prestataires = usePrestataires();
  const [dialogue, setDialogue] = useState<'vehicule' | 'prestataire' | null>(null);
  const peutCreer = peut(contexte, 'dechets', 'creer');
  const colonnesV: ColonneTableau<Vehicule>[] = [
    { id: 'code', entete: 'Code', enTeteLigne: true, masquable: false, cellule: (v) => <span className={commun.reference}>{v.code}</span>, valeurTri: (v) => v.code },
    { id: 'immat', entete: 'Immatriculation', cellule: (v) => v.immatriculation, valeurTri: (v) => v.immatriculation },
    { id: 'type', entete: 'Type', cellule: (v) => v.type },
    { id: 'capacite', entete: 'Capacité', numerique: true, cellule: (v) => (v.capacite_m3 === null ? 'non renseignée' : `${v.capacite_m3.toLocaleString('fr-FR')} m³`), valeurTri: (v) => v.capacite_m3 },
    { id: 'operateur', entete: 'Opérateur', cellule: (v) => v.prestataire?.nom ?? 'Régie communale' },
    { id: 'etat', entete: 'État', cellule: (v) => (v.actif ? <PuceEtat ton="ok" libelle="En service" /> : <PuceEtat ton="neutre" libelle="Retiré" />) },
  ];
  const colonnesP: ColonneTableau<Prestataire>[] = [
    { id: 'code', entete: 'Code', enTeteLigne: true, masquable: false, cellule: (p) => <span className={commun.reference}>{p.code}</span>, valeurTri: (p) => p.code },
    { id: 'nom', entete: 'Prestataire', cellule: (p) => p.nom, valeurTri: (p) => p.nom },
    { id: 'contact', entete: 'Contact', cellule: (p) => p.contact ?? 'non renseigné' },
    { id: 'contrat', entete: 'Contrat', cellule: (p) => p.contrat_reference ?? 'non renseigné' },
    { id: 'vehicules', entete: 'Véhicules', numerique: true, cellule: (p) => p.statistiques?.vehicules.toLocaleString('fr-FR') ?? '0' },
    { id: 'tournees', entete: 'Tournées', numerique: true, cellule: (p) => p.statistiques?.tournees.toLocaleString('fr-FR') ?? '0' },
  ];
  return (
    <>
      <EnTetePage etiquette="Déchets et propreté" titre="Véhicules et prestataires" description="Véhicules de la régie et des prestataires de collecte ; un prestataire est une fiche simple rattachée à ses véhicules, son équipe et ses tournées."
        actions={<><LienBouton href="/proprete" icone={ArrowLeft} variante="secondaire">Tournées</LienBouton>{peutCreer ? <Bouton variante="secondaire" icone={Plus} onClick={() => setDialogue('prestataire')}>Nouveau prestataire</Bouton> : null}{peutCreer ? <Bouton variante="principal" icone={Plus} onClick={() => setDialogue('vehicule')}>Nouveau véhicule</Bouton> : null}</>} />
      <EtatRequete chargement={vehicules.isPending} erreur={vehicules.error} onReessayer={() => void vehicules.refetch()} lot="L3-B" fonction="La liste des véhicules">
        {(vehicules.data ?? []).length === 0 ? <EtatVide titre="Aucun véhicule" icone={Truck}><p>Aucun véhicule de collecte n&apos;est enregistré.</p></EtatVide> : <Tableau legende="Véhicules" colonnes={colonnesV} lignes={vehicules.data ?? []} cleLigne={(v) => v.id} cleColonnes="proprete-vehicules" />}
      </EtatRequete>
      <EtatRequete chargement={prestataires.isPending} erreur={prestataires.error} onReessayer={() => void prestataires.refetch()} lot="L3-B" fonction="La liste des prestataires">
        {(prestataires.data ?? []).length === 0 ? <p className={commun.na}>Aucun prestataire enregistré.</p> : <Tableau legende="Prestataires" colonnes={colonnesP} lignes={prestataires.data ?? []} cleLigne={(p) => p.id} cleColonnes="proprete-prestataires" />}
      </EtatRequete>
      {dialogue === 'vehicule' ? <FormulaireVehicule prestataires={prestataires.data ?? []} onFermer={() => setDialogue(null)} /> : null}
      {dialogue === 'prestataire' ? <FormulairePrestataire onFermer={() => setDialogue(null)} /> : null}
    </>
  );
}
